import { useEffect, useRef, useState } from 'react'
import { useParams } from 'react-router-dom'
import {
  Loader2, AlertTriangle, Users, ClipboardList, Star,
  MessageSquareQuote, CalendarRange, CalendarDays, Clock, MapPin, BarChart3, Download, Image as ImageIcon,
  FileText, FileImage, FileType, ExternalLink, Upload, Trash2, Phone, Mail,
  Check, BadgeCheck, Hourglass, FileSignature, Megaphone, ListChecks, FileSearch,
  X, FolderDown, Quote, Printer,
} from 'lucide-react'
import { api } from '../lib/api.js'
import { fmtDateOnly } from '../lib/dt.js'
import PdfViewer from '../components/PdfViewer.jsx'
import { useConfirm } from '../components/ConfirmProvider.jsx'
import { OperationSummary, CstSatisfactionPanel, ReportPhotoSlider, OralHealthPanel } from '../components/ReportContent.jsx'

/**
 * HR Portal — mobile-first responsive design with the Tooth Taxi banner hero.
 *
 *   Tabs (click switches the panel — does NOT scroll):
 *     1. Documents   — generated docs (download ready-to-print) + company-provided uploads
 *     2. PR Media    — 1:1 + A4 + registration banner downloads
 *     3. Registration Data — link + employee roster + summary
 *     4. Report      — staff-published report (placeholder until Phase 5)
 *     5. Contact     — Tooth Taxi staff contact (เรด้า + เฟิสท์)
 */
export default function HrReport() {
  const { token } = useParams()
  const [data, setData]   = useState(null)
  const [error, setError] = useState(null)
  const [tick, bump]      = useState(0)
  const [tab, setTab]     = useState('docs')

  function reload() {
    api.publicSurvey.report.get(token)
      .then(setData)
      .catch((e) => setError(extractErr(e)))
  }
  useEffect(reload, [token, tick])     // eslint-disable-line react-hooks/exhaustive-deps

  if (error && !data) return <Shell><ErrorCard message={error} /></Shell>
  if (!data)          return <Shell><Centered><Loader2 className="animate-spin text-amber-400" /></Centered></Shell>

  return (
    <Shell>
      {/* On-screen app (hidden when printing) */}
      <div className="print:hidden">
        <Hero o={data.operation} />
        <TabBar value={tab} onChange={setTab} />
        {tab === 'docs'    && <DocumentsSection  token={token} documents={data.documents} onChange={() => bump((n) => n + 1)} />}
        {tab === 'pr'      && <PrMediaSection    pr_image={data.pr_image} pr_main_sq={data.pr_main_sq} pr_main_a4={data.pr_main_a4} />}
        {tab === 'regdata' && <RegistrationSection registration={data.registration} expectedPatients={data.operation.expected_patients} />}
        {tab === 'report'  && <ReportSection cst={data.cst} photos={data.report_photos || []} token={token} approved={data.report_approved !== false} operation={data.operation} regCount={data.operation?.treated_patients ?? 0} oralHealth={data.oral_health} />}
        {tab === 'contact' && <ContactSection />}
        <Footer />
      </div>

      {/* A4 print/PDF layout — rendered off-screen, shown only when printing */}
      <ReportPrintArea data={data} />
    </Shell>
  )
}


/* ─────────────────────────── Hero + tab bar ─────────────────────────── */

function Hero({ o }) {
  return (
    <section className="mb-4 md:mb-5">
      <div className="rounded-2xl md:rounded-3xl overflow-hidden shadow-[0_20px_50px_-20px_rgba(217,119,6,0.35)]">
        <img src="/hr-portal-banner.png" alt="Tooth Taxi HR / Company Site"
          className="block w-full h-auto" loading="eager" />
      </div>
      <div className="mt-3 md:mt-4 bg-white rounded-2xl md:rounded-3xl shadow-xl ring-1 ring-amber-100/70 p-4 md:p-5">
        <div className="flex items-start gap-3 md:gap-4">
          {o.company_logo_url ? (
            <img src={o.company_logo_url} alt=""
              className="w-14 h-14 md:w-16 md:h-16 rounded-xl object-cover ring-1 ring-zinc-200 bg-white shrink-0" />
          ) : (
            <div className="w-14 h-14 md:w-16 md:h-16 rounded-xl bg-amber-50 ring-1 ring-amber-200 text-amber-700 inline-flex items-center justify-center text-xl md:text-2xl font-bold shrink-0">
              {firstInitial(o.company_name)}
            </div>
          )}
          <div className="min-w-0 flex-1">
            <div className="text-[11.5px] md:text-[12px] uppercase tracking-[0.18em] text-amber-700 font-semibold">บริษัท</div>
            <h1 className="text-[20px] md:text-[24px] font-extrabold text-zinc-900 leading-tight truncate">{o.company_name}</h1>
            <div className="mt-1.5 md:mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-[12.5px] md:text-[14px] text-zinc-600">
              <span className="inline-flex items-center gap-1.5"><CalendarRange size={12} className="text-amber-500" /> {fmtHeroRange(o.start_date, o.end_date, o.start_time, o.end_time)}</span>
              {o.location_province && <span className="inline-flex items-center gap-1.5"><MapPin size={12} className="text-amber-500" /> {o.location_province}</span>}
              {o.expected_patients != null && o.expected_patients > 0 && (
                <span className="inline-flex items-center gap-1.5"><Users size={12} className="text-amber-500" /> ขั้นต่ำ {Number(o.expected_patients).toLocaleString()} คน</span>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  )
}

const SECTIONS = [
  { id: 'docs',     label: 'เอกสาร',      icon: FileSignature },
  { id: 'pr',       label: 'PR Media',     icon: Megaphone     },
  { id: 'regdata',  label: 'ลงทะเบียน',     icon: ListChecks    },
  { id: 'report',   label: 'รายงาน',       icon: FileSearch    },
  { id: 'contact',  label: 'ติดต่อ',       icon: Phone         },
]

function TabBar({ value, onChange }) {
  return (
    <nav className="sticky top-2 z-20 mb-4 md:mb-5">
      <div className="bg-white/95 backdrop-blur rounded-2xl shadow-sm ring-1 ring-zinc-200 px-1 py-1 flex items-center gap-0.5 md:gap-1 overflow-x-auto scrollbar-hide">
        {SECTIONS.map((s) => {
          const active = s.id === value
          return (
            <button key={s.id} type="button" onClick={() => onChange(s.id)}
              className={'inline-flex items-center gap-1 md:gap-1.5 px-2.5 md:px-3 py-2 md:py-2 rounded-md text-[13.5px] md:text-[14.5px] font-medium whitespace-nowrap shrink-0 transition ' +
                (active
                  ? 'bg-amber-500 text-white shadow-sm'
                  : 'text-zinc-700 hover:bg-amber-50 hover:text-amber-700')}>
              <s.icon size={13} /> <span>{s.label}</span>
            </button>
          )
        })}
      </div>
    </nav>
  )
}


/* ─────────────────────────── 1. Documents ─────────────────────────── */

function DocumentsSection({ token, documents, onChange }) {
  // HR portal only shows the two HR-facing categories. Internal categories
  // (e.g. สสจ paperwork our staff prepares for the health office) are hidden
  // here so we don't surface paperwork HR can't act on.
  const HR_VISIBLE_CATS = new Set(['signing-prep', 'company-provided'])
  const hrDocs = documents.filter((d) => !d.kind_category || HR_VISIBLE_CATS.has(d.kind_category))
  const generated = hrDocs.filter((d) => d.kind_type === 'generated')
  const provided  = hrDocs.filter((d) => d.kind_type === 'company_provided')

  // ZIP is the intended primary download path — only enabled once at least
  // one generated doc has been approved by Tooth Taxi staff. We never want HR
  // to bundle in-review drafts.
  const zipReady = generated.some((d) => d.approved && !!d.generated_url)

  return (
    <SectionShell id="docs" icon={FileSignature} title="เอกสารต่าง ๆ"
      subtitle="Tooth Taxi ให้บริการภายใต้มาตรฐานของหน่วยงานราชการ — เพื่อขออนุญาตเข้าให้บริการได้อย่างถูกต้อง รบกวนคุณช่วยจัดเตรียมและส่งเอกสารด้านล่างให้ทีมงานครับ/ค่ะ">
      <div className="space-y-6">
        <div>
          <SubsectionTitle>เตรียมเอกสารเพื่อลงนาม</SubsectionTitle>
          {generated.length > 0 && (
            <div className="mb-3 flex flex-wrap items-center gap-2.5">
              <a href={api.publicSurvey.report.zipGeneratedUrl(token)}
                aria-disabled={!zipReady}
                onClick={(e) => { if (!zipReady) e.preventDefault() }}
                className={'inline-flex items-center gap-1.5 px-3 py-2 rounded-md text-[14px] font-medium min-h-[36px] shadow-sm transition ' +
                  (zipReady
                    ? 'bg-amber-500 hover:bg-amber-600 text-white shadow-amber-500/20'
                    : 'bg-zinc-200 text-zinc-400 cursor-not-allowed')}>
                <FolderDown size={13} /> ดาวน์โหลดทั้งหมด (.zip)
              </a>
              <span className="text-[12.5px] text-zinc-500 leading-snug">
                {zipReady
                  ? 'รวมเอกสารที่ Approve แล้วทั้งหมดเป็นไฟล์ zip — แนะนำให้ใช้ปุ่มนี้'
                  : 'ปุ่มจะใช้งานได้เมื่อ Tooth Taxi Approve เอกสารแล้ว'}
              </span>
            </div>
          )}
          {generated.length === 0 ? <Empty text="ยังไม่มีเอกสารในหมวดนี้" /> : (
            <ul className="divide-y divide-zinc-100">
              {generated.map((d) => (
                <li key={d.id} className="py-3.5 first:pt-1 last:pb-0">
                  <DocRow token={token} request={d} mode="generated" onChange={onChange} />
                </li>
              ))}
            </ul>
          )}
          {generated.length > 0 && <UploadHint />}
        </div>

        <div>
          <SubsectionTitle>เอกสารจากบริษัท</SubsectionTitle>
          {provided.length === 0 ? <Empty text="ยังไม่มีรายการที่ขอ" /> : (
            <ul className="divide-y divide-zinc-100">
              {provided.map((d) => (
                <li key={d.id} className="py-3.5 first:pt-1 last:pb-0">
                  <DocRow token={token} request={d} mode="provided" onChange={onChange} />
                </li>
              ))}
            </ul>
          )}
          {provided.length > 0 && <UploadHint />}
        </div>
      </div>
    </SectionShell>
  )
}

/* Single shared upload-rules line — replaces the per-row caption that used to
   repeat under every UploadPanel. Rendered once at the bottom of each
   subsection so HR sees the rules in context without the visual noise. */
function UploadHint() {
  return (
    <p className="mt-3 text-[11.5px] text-zinc-400 leading-snug">
      อัปโหลดได้: JPG / PNG / PDF · ≤ 10 MB · เก็บได้สูงสุด 5 ไฟล์ต่อรายการ · ลากไฟล์มาวางหรือคลิก +
    </p>
  )
}

function DocRow({ token, request: r, mode, onChange }) {
  const isGenerated = mode === 'generated'
  // Coerce to boolean — D1 returns approved as integer 0/1, and `{0 && <X/>}`
  // would render a stray "0" into the upload panel grid.
  const ready = Boolean(r.approved && r.generated_url)
  const liveFiles = r.files || []
  const hasFiles = liveFiles.length > 0

  return (
    <div>
      {/* Title row — same for both modes */}
      <div className="flex items-start gap-2.5 md:gap-3">
        {isGenerated
          ? <FileText  size={18} className="text-amber-500 shrink-0 mt-0.5" />
          : <FileImage size={18} className="text-amber-500 shrink-0 mt-0.5" />}
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-[15px] md:text-[15px] font-semibold text-zinc-900">{displayLabel(r)}</span>
            {isGenerated
              ? <DocStatusPill state={hasFiles ? 'received' : ready ? 'waiting' : 'preparing'} />
              : <DocStatusPill state={hasFiles ? 'received-n' : 'upload'} count={liveFiles.length} />}
          </div>
          {r.note && <p className="mt-0.5 text-[13.5px] text-zinc-500 italic">{r.note}</p>}
        </div>
      </div>

      {/* Generated: two-column workflow (download left → upload right).
          Provided: just the upload tile grid. */}
      <div className="mt-3 ml-[26px]">
        {isGenerated ? (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 items-stretch">
            <DownloadPanel ready={ready} url={r.generated_url} />
            <UploadPanel
              token={token} reqId={r.id} files={liveFiles}
              canUpload={ready} onChange={onChange}
              step="2"
            />
          </div>
        ) : (
          <UploadPanel
            token={token} reqId={r.id} files={liveFiles}
            canUpload onChange={onChange}
          />
        )}
      </div>
    </div>
  )
}

/* ── Left side: thumbnail → preview popup + a small secondary download link.
   The primary download path is the section-level ZIP button; this per-file
   link stays as a fallback for cases when HR just wants one specific doc. */
function DownloadPanel({ ready, url }) {
  const [previewing, setPreviewing] = useState(false)
  const driveId = driveIdFromUrl(url)
  const inlineUrl = driveId ? `https://drive.google.com/file/d/${driveId}/preview` : url
  const dlUrl     = driveId ? `https://drive.google.com/uc?export=download&id=${driveId}` : url

  return (
    <div className="rounded-xl ring-1 ring-zinc-200 bg-zinc-50/60 p-3 md:p-3.5 flex flex-col">
      <div className="text-[12px] uppercase tracking-wider font-semibold text-zinc-500 mb-2.5 inline-flex items-center gap-1.5">
        <span className="inline-flex items-center justify-center w-4 h-4 rounded-full bg-zinc-200 text-zinc-700 text-[11px] font-bold">1</span>
        ดาวน์โหลด
      </div>
      {ready ? (
        <div className="flex items-center gap-3">
          <button type="button" onClick={() => setPreviewing(true)}
            title="คลิกเพื่อพรีวิว"
            className="shrink-0 w-16 h-20 rounded-md bg-white ring-1 ring-zinc-200 hover:ring-amber-400 hover:shadow-sm overflow-hidden inline-flex items-center justify-center transition">
            <DriveThumb id={driveId} />
          </button>
          <a href={dlUrl} target="_blank" rel="noreferrer"
            className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-md ring-1 ring-zinc-300 bg-white hover:bg-zinc-50 hover:ring-zinc-400 text-zinc-700 text-[13.5px] font-medium">
            <Download size={11} /> ดาวน์โหลดไฟล์นี้
          </a>
        </div>
      ) : (
        <div className="flex-1 flex items-center">
          <p className="text-[12.5px] text-zinc-500 italic leading-snug">
            {url ? 'เอกสารยังอยู่ระหว่างการตรวจสอบโดยทีม Tooth Taxi' : 'ทีม Tooth Taxi กำลังจัดเตรียมเอกสาร — โปรดรอ'}
          </p>
        </div>
      )}
      {previewing && (
        <HrPreviewModal
          filename="เอกสารสำหรับลงนาม.pdf"
          contentType="application/pdf"
          inlineUrl={inlineUrl}
          externalUrl={url}
          downloadUrl={dlUrl}
          onClose={() => setPreviewing(false)}
        />
      )}
    </div>
  )
}

/* 64×80 Drive thumbnail with a clean PDF-icon fallback — matches the webapp's
   LeftDocCard thumb. Drive's thumbnail endpoint sometimes returns 404 for
   "Anyone with the link" files; we fall back to a styled amber PDF tile. */
function DriveThumb({ id }) {
  const [errored, setErrored] = useState(false)
  if (!id || errored) {
    return (
      <div className="w-full h-full bg-gradient-to-br from-amber-50 to-orange-100 flex flex-col items-center justify-center text-amber-700">
        <FileType size={22} />
        <span className="text-[10px] font-bold mt-0.5 tracking-wider">PDF</span>
      </div>
    )
  }
  return (
    <img src={`https://drive.google.com/thumbnail?id=${id}&sz=w128`}
      alt="" loading="lazy" referrerPolicy="no-referrer"
      className="w-full h-full object-cover"
      onError={() => setErrored(true)} />
  )
}

// "https://drive.google.com/file/d/<id>/view?usp=drivesdk"  →  "<id>"
// or "?id=<id>&...". Returns null on no match.
function driveIdFromUrl(url) {
  if (!url || typeof url !== 'string') return null
  const m1 = url.match(/\/d\/([a-zA-Z0-9_-]+)/)
  if (m1) return m1[1]
  const m2 = url.match(/[?&]id=([a-zA-Z0-9_-]+)/)
  if (m2) return m2[1]
  return null
}

/* ── Right side: thumbnail grid + upload tile.
   Per-row JPG / size caption is intentionally absent here — it's shown once
   per subsection via <UploadHint> so the cards stay clean. */
function UploadPanel({ token, reqId, files, canUpload, onChange, step }) {
  const fileInputRef = useRef(null)
  const confirm = useConfirm()
  const [busy, setBusy] = useState(false)
  const [err, setErr]   = useState(null)
  const [dragOver, setDragOver] = useState(false)
  const atCap = files.length >= 5
  const canDrop = Boolean(canUpload) && !atCap && !busy

  function pickFile() { fileInputRef.current?.click() }
  async function uploadFile(file) {
    if (!file) return
    if (atCap)                                                        { setErr('ไฟล์ครบ 5 ไฟล์แล้ว'); return }
    if (file.size > 10 * 1024 * 1024)                                 { setErr('ไฟล์ใหญ่เกิน 10 MB'); return }
    if (!/^image\/(jpeg|png)$|^application\/pdf$/.test(file.type))    { setErr('รองรับเฉพาะ JPG, PNG, PDF'); return }
    setBusy(true); setErr(null)
    try {
      const dataUri = await fileToDataUri(file)
      await api.publicSurvey.report.uploadFile(token, reqId, { data: dataUri, filename: file.name })
      onChange()
    } catch (e) { setErr(extractErr(e)) }
    finally     { setBusy(false) }
  }
  async function onFileChange(e) {
    const file = e.target.files?.[0]
    if (e.target) e.target.value = ''
    await uploadFile(file)
  }
  function onDragOver(e)  { e.preventDefault(); if (canDrop) setDragOver(true) }
  function onDragLeave(e) { e.preventDefault(); setDragOver(false) }
  async function onDrop(e) {
    e.preventDefault(); setDragOver(false)
    if (!canDrop) return
    await uploadFile(e.dataTransfer?.files?.[0])
  }
  async function removeFile(f) {
    const ok = await confirm({
      title:        'ลบไฟล์',
      message:      `"${f.filename}" จะถูกลบและไม่สามารถกู้คืนได้`,
      confirmLabel: 'ลบ',
      tone:         'danger',
    })
    if (!ok) return
    setBusy(true); setErr(null)
    try { await api.publicSurvey.report.deleteFile(token, reqId, f.id); onChange() }
    catch (e) { setErr(extractErr(e)) }
    finally   { setBusy(false) }
  }

  return (
    <div onDragOver={onDragOver} onDragLeave={onDragLeave} onDrop={onDrop}
      className={'rounded-xl ring-1 p-3 md:p-3.5 flex flex-col transition ' +
        (dragOver
          ? 'ring-2 ring-amber-500 bg-amber-100/60'
          : 'ring-amber-200/70 bg-amber-50/30')}>
      <div className="text-[12px] uppercase tracking-wider font-semibold text-amber-800 mb-2.5 inline-flex items-center gap-1.5">
        {step && (
          <span className="inline-flex items-center justify-center w-4 h-4 rounded-full bg-amber-500 text-white text-[11px] font-bold">{step}</span>
        )}
        อัปโหลด{step ? 'ที่เซ็นแล้ว' : ''}
      </div>

      <div className="flex flex-wrap gap-2 items-start">
        {files.map((f) => (
          <HrFileTile key={f.id} token={token} reqId={reqId} f={f}
            onRemove={() => removeFile(f)} busy={busy} />
        ))}
        {canUpload && !atCap ? (
          <UploadTile onClick={pickFile} busy={busy} dragOver={dragOver} />
        ) : !canUpload && files.length === 0 ? (
          <div className="w-16 h-20 rounded-md border-2 border-dashed border-zinc-200 text-zinc-300 flex items-center justify-center"
            title="รอเอกสารจาก Tooth Taxi ก่อน">
            <Upload size={16} />
          </div>
        ) : null}
      </div>

      <input ref={fileInputRef} type="file"
        accept="image/jpeg,image/png,application/pdf,.jpg,.jpeg,.png,.pdf"
        className="hidden" onChange={onFileChange} />

      {err && (
        <div className="mt-2 px-2 py-1.5 rounded-md bg-rose-50 ring-1 ring-rose-200 text-rose-700 text-[12.5px] inline-flex items-center gap-1.5">
          <AlertTriangle size={11} /> {err}
        </div>
      )}
    </div>
  )
}

/* Clickable thumbnail tile — image preview for images, amber PDF tile otherwise.
   Click opens an in-app preview modal (HrPreviewModal). The modal renders
   PDFs via a same-origin proxy URL (PDF.js canvas) instead of an iframe pointed
   at Wasabi, so privacy extensions / tracker shields can't block the embed. */
function HrFileTile({ token, reqId, f, onRemove, busy }) {
  const [imgFailed, setImgFailed] = useState(false)
  const [previewing, setPreviewing] = useState(false)
  const isImage = typeof f.content_type === 'string' && f.content_type.startsWith('image/')
  const isPdf   = f.content_type === 'application/pdf'
  const proxyUrl = api.publicSurvey.report.filePreviewUrl(token, reqId, f.id)

  return (
    <div className="group relative w-16">
      <button type="button" onClick={() => setPreviewing(true)} title={f.filename}
        className="block w-16 h-20 rounded-md overflow-hidden ring-1 ring-zinc-200 bg-white hover:ring-amber-400 hover:shadow-sm transition">
        {isImage && f.url && !imgFailed ? (
          <img src={f.url} alt={f.filename} loading="lazy"
            className="w-full h-full object-cover"
            onError={() => setImgFailed(true)} />
        ) : isPdf ? (
          <div className="w-full h-full bg-gradient-to-br from-amber-50 to-orange-100 flex flex-col items-center justify-center text-amber-700">
            <FileType size={20} />
            <span className="text-[10px] font-bold mt-0.5 tracking-wider">PDF</span>
          </div>
        ) : (
          <div className="w-full h-full bg-gradient-to-br from-amber-50 to-orange-100 flex flex-col items-center justify-center text-amber-700">
            <FileText size={20} />
            <span className="text-[10px] font-bold mt-0.5 tracking-wider">FILE</span>
          </div>
        )}
      </button>
      <button onClick={onRemove} disabled={busy} title="ลบ" aria-label="ลบไฟล์"
        className="absolute -top-1.5 -right-1.5 w-5 h-5 rounded-full bg-rose-500 hover:bg-rose-600 text-white flex items-center justify-center shadow disabled:opacity-50 opacity-0 group-hover:opacity-100 focus:opacity-100 transition-opacity">
        <X size={11} />
      </button>
      <div className="mt-1 text-[11px] text-zinc-500 truncate" title={f.filename}>
        {f.filename}
      </div>
      {previewing && (
        <HrPreviewModal
          filename={f.filename}
          contentType={f.content_type}
          inlineUrl={proxyUrl}
          externalUrl={f.url}
          downloadUrl={f.download_url || f.url}
          onClose={() => setPreviewing(false)}
        />
      )}
    </div>
  )
}

/* Dashed plus-tile that triggers the file picker. Matches the thumbnail size
   so the upload action sits naturally in the same grid. Lights up while a
   file is being dragged over the parent UploadPanel. */
function UploadTile({ onClick, busy, dragOver }) {
  return (
    <button type="button" onClick={onClick} disabled={busy} title="อัปโหลดไฟล์"
      className={'w-16 h-20 rounded-md border-2 border-dashed flex flex-col items-center justify-center transition disabled:opacity-50 ' +
        (dragOver
          ? 'border-amber-500 bg-amber-50 text-amber-700'
          : 'border-amber-300 hover:border-amber-500 hover:bg-amber-50 text-amber-700')}>
      {busy
        ? <Loader2 size={16} className="animate-spin" />
        : <><Upload size={16} /><span className="text-[10px] font-medium mt-0.5">{dragOver ? 'วางที่นี่' : 'อัปโหลด'}</span></>}
    </button>
  )
}

/* Fullscreen preview modal — matches the webapp's FilePreviewModal.
   Routing:
     - images           → <img>
     - Drive embed URL  → iframe (drive.google.com serves an embed page, never raw bytes)
     - everything else  → PDF.js (canvas) via PdfViewer, fed a same-origin URL.
   The PDF.js branch is why we proxy Wasabi-hosted PDFs through our own
   /api/survey/report/.../preview endpoint instead of feeding a presigned URL
   directly — privacy extensions / tracker shields would block the latter. */
function HrPreviewModal({ filename, contentType, inlineUrl, externalUrl, downloadUrl, onClose }) {
  useEffect(() => {
    const onEsc = (e) => { if (e.key === 'Escape') onClose() }
    document.addEventListener('keydown', onEsc)
    return () => document.removeEventListener('keydown', onEsc)
  }, [onClose])

  const isImage      = typeof contentType === 'string' && contentType.startsWith('image/')
  const isDriveEmbed = /(?:drive|docs?)\.google\.com\//.test(inlineUrl || '')

  return (
    <div className="fixed inset-0 z-[80] flex flex-col bg-zinc-900/85 backdrop-blur-sm">
      <header className="flex items-center gap-2 px-3 md:px-4 py-2.5 bg-zinc-900/60 text-white">
        <span className="text-[14px] md:text-[14.5px] truncate flex-1 font-medium" title={filename}>{filename || 'preview'}</span>
        {downloadUrl && (
          <a href={downloadUrl} download={filename || true}
            className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md text-[13.5px] bg-white/10 hover:bg-white/20 transition">
            <Download size={12} /> ดาวน์โหลด
          </a>
        )}
        {externalUrl && (
          <a href={externalUrl} target="_blank" rel="noreferrer"
            className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md text-[13.5px] bg-white/10 hover:bg-white/20 transition">
            <ExternalLink size={12} /> เปิดในแท็บใหม่
          </a>
        )}
        <button onClick={onClose}
          className="w-8 h-8 rounded-md inline-flex items-center justify-center hover:bg-white/15">
          <X size={18} />
        </button>
      </header>

      <div onClick={onClose} className="flex-1 overflow-auto flex items-center justify-center p-3 md:p-6">
        {!inlineUrl ? (
          <div className="text-white/80 text-center text-[14.5px] max-w-[320px]">
            ยังไม่มีลิงก์สำหรับ preview — ใช้ปุ่ม "เปิดในแท็บใหม่" หรือ "ดาวน์โหลด" ด้านบน
          </div>
        ) : isImage ? (
          <img src={inlineUrl} alt={filename}
            onClick={(e) => e.stopPropagation()}
            className="max-w-full max-h-full object-contain rounded-md shadow-2xl" />
        ) : isDriveEmbed ? (
          <div onClick={(e) => e.stopPropagation()}
            className="relative w-full h-full max-w-[1100px] bg-white rounded-md shadow-2xl overflow-hidden">
            <iframe src={inlineUrl} title={filename}
              className="w-full h-full bg-white"
              sandbox="allow-scripts allow-same-origin allow-popups allow-downloads" />
          </div>
        ) : (
          <PdfViewer url={inlineUrl} />
        )}
      </div>
    </div>
  )
}

function DocStatusPill({ state, count }) {
  const map = {
    'preparing':  { tone: 'zinc',    icon: Hourglass, label: 'กำลังเตรียม' },
    'waiting':    { tone: 'amber',   icon: Hourglass, label: 'รอเซ็น' },
    'received':   { tone: 'emerald', icon: Check,     label: 'Uploaded' },
    'upload':     { tone: 'amber',   icon: Upload,    label: 'กรุณาอัปโหลด' },
    'received-n': { tone: 'emerald', icon: Check,     label: `${count} ไฟล์` },
  }[state] || {}
  const cls = {
    amber:   'bg-amber-50 text-amber-800',
    emerald: 'bg-emerald-50 text-emerald-700',
    zinc:    'bg-zinc-100 text-zinc-600',
  }[map.tone] || ''
  const I = map.icon
  return (
    <span className={'inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[11.5px] font-medium ' + cls}>
      {I && <I size={10} />} {map.label}
    </span>
  )
}


/* ─────────────────────────── 2. PR Media ─────────────────────────── */

function PrMediaSection({ pr_image, pr_main_sq, pr_main_a4 }) {
  const promoteItems = [
    { label: 'PR Square 1:1', desc: 'Facebook / LINE / IG', img: pr_main_sq, ratio: '1/1' },
    { label: 'PR A4 portrait', desc: 'โปสเตอร์ A4',        img: pr_main_a4, ratio: '1055/1491' },
  ].filter((it) => it.img?.url)

  const hasBanner   = !!pr_image?.url
  const hasPromote  = promoteItems.length > 0

  return (
    <SectionShell id="pr" icon={Megaphone} title="PR Media"
      subtitle="ไฟล์ภาพประชาสัมพันธ์ที่ทีม Tooth Taxi จัดทำให้พร้อมใช้ — เผยแพร่ผ่านช่องทางต่าง ๆ ของบริษัท เพื่อเชิญชวนพนักงานเข้ารับบริการได้สะดวกขึ้น">

      {/* Each subsection is rendered only when its image(s) actually exist.
          Empty subsections are hidden entirely — HR shouldn't see placeholder
          copy for things Tooth Taxi hasn't produced yet. */}

      {hasBanner && (
        <div className={hasPromote ? 'mb-6' : ''}>
          <SubsectionTitle>เปิดรับลงทะเบียน</SubsectionTitle>
          <p className="text-[13.5px] md:text-[14px] text-zinc-600 leading-relaxed mb-3 max-w-[640px]">
            ดาวน์โหลด<strong className="text-zinc-800">ภาพแบนเนอร์พร้อม QR code</strong>
            ไปติดบอร์ดประชาสัมพันธ์ในออฟฟิศ หรือโพสต์ในแชทกลุ่ม — พนักงานสแกนแล้วลงทะเบียนได้ทันที
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <RegistrationBannerCard img={pr_image} />
          </div>
        </div>
      )}

      {hasPromote && (
        <div>
          <SubsectionTitle>โปรโมตกิจกรรม</SubsectionTitle>
          <p className="text-[13.5px] md:text-[14px] text-zinc-600 leading-relaxed mb-3 max-w-[640px]">
            เพื่อให้พนักงานทราบ<strong className="text-zinc-800">วันและสถานที่ให้บริการ</strong> เราจัดทำภาพประชาสัมพันธ์ไว้ 2 ขนาดให้คุณ —
            โพสต์เวอร์ชัน <strong className="text-zinc-800">1:1</strong> ลงโซเชียลในกลุ่มพนักงาน
            และพิมพ์เวอร์ชัน <strong className="text-zinc-800">A4</strong>
            ติดในจุดที่พนักงานผ่านบ่อย เช่น โรงอาหารหรือลิฟต์ — ยิ่งโปรโมตมาก ยิ่งช่วยให้พนักงานได้รับประโยชน์จากบริการเต็มที่
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {promoteItems.map((it) => <PrImageCard key={it.label} item={it} />)}
          </div>
        </div>
      )}

      {!hasBanner && !hasPromote && (
        <Empty text="ทีม Tooth Taxi ยังไม่ได้สร้างภาพประชาสัมพันธ์" />
      )}
    </SectionShell>
  )
}

function RegistrationLinkCard({ link }) {
  const [copied, setCopied] = useState(false)
  async function copy() {
    if (!link) return
    try {
      await navigator.clipboard.writeText(link)
      setCopied(true); setTimeout(() => setCopied(false), 1600)
    } catch { /* no-op */ }
  }
  return (
    <div className="rounded-xl ring-1 ring-amber-200/70 bg-gradient-to-br from-amber-50/60 to-orange-50/30 p-3.5 md:p-4 flex flex-col">
      <div className="text-[12px] uppercase tracking-wider font-semibold text-amber-700 mb-1.5 inline-flex items-center gap-1.5">
        <ExternalLink size={11} /> ลิงก์ลงทะเบียน
      </div>
      {link ? (
        <>
          <input value={link} readOnly onFocus={(e) => e.target.select()}
            className="w-full px-2 py-2 rounded-md bg-white ring-1 ring-zinc-200 text-[12.5px] md:text-[13.5px] tabular-nums text-zinc-700" />
          <div className="mt-2 flex items-center gap-2">
            <button type="button" onClick={copy}
              className="inline-flex items-center gap-1.5 px-3 py-2 rounded-md bg-amber-500 hover:bg-amber-600 text-white text-[14px] font-medium min-h-[36px]">
              {copied ? <Check size={13} /> : <Download size={13} />}
              {copied ? 'คัดลอกแล้ว' : 'คัดลอกลิงก์'}
            </button>
            <a href={link} target="_blank" rel="noreferrer"
              className="inline-flex items-center gap-1 px-3 py-2 rounded-md ring-1 ring-amber-200 text-amber-800 bg-white hover:bg-amber-50 text-[13.5px] font-medium min-h-[36px]">
              <ExternalLink size={11} /> เปิด
            </a>
          </div>
        </>
      ) : (
        <p className="text-[13.5px] text-zinc-500 italic">ทีม Tooth Taxi ยังไม่ได้เปิดลิงก์สำหรับลงทะเบียน</p>
      )}
    </div>
  )
}

function RegistrationBannerCard({ img }) {
  if (!img?.url) {
    return (
      <div className="rounded-xl ring-1 ring-zinc-200 bg-zinc-50/40 p-4 flex items-center justify-center min-h-[140px]">
        <span className="text-[13.5px] text-zinc-400 italic">ยังไม่มีแบนเนอร์ลงทะเบียน</span>
      </div>
    )
  }
  return (
    <PrCard
      img={img}
      label="แบนเนอร์ลงทะเบียน"
      desc="พร้อม QR code · พิมพ์ติดบอร์ด"
      ratio="1587/2245"
    />
  )
}

function PrImageCard({ item: it }) {
  return (
    <PrCard
      img={it.img}
      label={it.label}
      desc={it.desc}
      ratio={it.ratio}
    />
  )
}

/* Shared card for every PR Media tile. Click the image → fullscreen preview
   (HrPreviewModal). The "Download" button is a separate explicit action so
   HR doesn't have to right-click "save image as" after closing the preview. */
function PrCard({ img, label, desc, ratio }) {
  const [previewing, setPreviewing] = useState(false)
  return (
    <>
      <div className="group rounded-xl ring-1 ring-amber-200/60 bg-gradient-to-br from-amber-50/30 to-orange-50/10 overflow-hidden hover:ring-amber-400 hover:shadow-lg transition flex flex-col">
        <button type="button" onClick={() => setPreviewing(true)}
          title="คลิกเพื่อพรีวิว"
          className="block bg-white overflow-hidden active:scale-[0.99] focus:outline-none focus-visible:ring-2 focus-visible:ring-amber-400">
          <img src={img.url} alt={label}
            className="block w-full h-auto" style={{ aspectRatio: ratio }} />
        </button>
        <div className="p-3 flex items-center justify-between gap-2">
          <div className="min-w-0">
            <div className="text-[15px] font-semibold text-zinc-900 truncate">{label}</div>
            {desc && <div className="text-[13px] text-zinc-500 truncate">{desc}</div>}
          </div>
          <a href={img.download_url || img.url} download
            className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-md bg-amber-500 hover:bg-amber-600 text-white text-[13px] font-medium shrink-0 shadow-sm shadow-amber-500/20">
            <Download size={12} /> Download
          </a>
        </div>
      </div>
      {previewing && (
        <HrPreviewModal
          filename={label}
          contentType="image/png"
          inlineUrl={img.url}
          externalUrl={img.url}
          downloadUrl={img.download_url || img.url}
          onClose={() => setPreviewing(false)}
        />
      )}
    </>
  )
}


/* ─────────────────────────── 3. Registration ─────────────────────────── */

function RegistrationSection({ registration, expectedPatients }) {
  const { count, link, list } = registration
  const target = Number(expectedPatients) > 0 ? Number(expectedPatients) : null
  return (
    <SectionShell id="regdata" icon={ListChecks} title="ลงทะเบียนพนักงาน"
      subtitle="คัดลอกลิงก์เพื่อส่งให้พนักงาน และตรวจสอบรายชื่อพนักงานที่ลงทะเบียนเข้ารับบริการแล้ว">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-4">
        <div className="rounded-2xl ring-1 ring-amber-200/70 bg-gradient-to-br from-amber-50 to-orange-50 px-4 py-3 text-center flex flex-col items-center justify-center">
          <Users size={18} className="text-amber-600" />
          <div className="mt-1.5 leading-none">
            <span className="text-[28px] md:text-[30px] font-extrabold tabular-nums text-zinc-900">{count.toLocaleString()}</span>
            {target != null && (
              <span className="text-[18px] md:text-[20px] font-semibold tabular-nums text-amber-700/80"> / {target.toLocaleString()}</span>
            )}
          </div>
          <div className="text-[12.5px] text-zinc-600 mt-1">
            {target != null ? `ลงทะเบียนแล้ว · ขั้นต่ำ ${target.toLocaleString()} คน` : 'พนักงานลงทะเบียนแล้ว'}
          </div>
        </div>
        <div className="md:col-span-2">
          <RegistrationLinkCard link={link} />
        </div>
      </div>

      {/* Roster — table on desktop, card-list on mobile */}
      {list.length === 0 ? (
        <Empty text="ยังไม่มีพนักงานลงทะเบียน" />
      ) : (
        <>
          {/* Desktop table */}
          <div className="hidden md:block overflow-x-auto rounded-xl ring-1 ring-zinc-200">
            <table className="w-full text-[14px]">
              <thead className="bg-zinc-50/70 text-[11.5px] uppercase tracking-wider font-semibold text-zinc-500">
                <tr>
                  <th className="px-3 py-2 text-left">วันที่ลงทะเบียน</th>
                  <th className="px-3 py-2 text-left">ชื่อ-สกุล</th>
                  <th className="px-3 py-2 text-left">โทร</th>
                  <th className="px-3 py-2 text-left">บัตร ปชช.</th>
                  <th className="px-3 py-2 text-left">ช่วงเวลา</th>
                </tr>
              </thead>
              <tbody>
                {list.map((r) => (
                  <tr key={r.id} className="border-b border-zinc-100 last:border-b-0">
                    <td className="px-3 py-2 text-zinc-500 tabular-nums">{fmtFull(r.created_at)}</td>
                    <td className="px-3 py-2 text-zinc-900">{r.fullname}</td>
                    <td className="px-3 py-2 text-zinc-700 tabular-nums">{r.phone}</td>
                    <td className="px-3 py-2 text-zinc-500 tabular-nums">{r.idcard}</td>
                    <td className="px-3 py-2 text-zinc-700">{slotLabelSplit(r) || slotLabel(r.time_slot)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Mobile cards */}
          <ul className="md:hidden space-y-2">
            {list.map((r) => (
              <li key={r.id} className="rounded-xl ring-1 ring-zinc-200 bg-white p-3">
                <div className="flex items-center justify-between gap-2 mb-1">
                  <span className="text-[14.5px] font-semibold text-zinc-900 truncate">{r.fullname}</span>
                  <span className="text-[11.5px] text-zinc-400 tabular-nums shrink-0">{fmtFull(r.created_at)}</span>
                </div>
                <div className="grid grid-cols-2 gap-x-3 gap-y-0.5 text-[12.5px]">
                  <span className="text-zinc-500">โทร</span>
                  <span className="text-zinc-900 tabular-nums">{r.phone}</span>
                  <span className="text-zinc-500">บัตร</span>
                  <span className="text-zinc-700 tabular-nums truncate">{r.idcard}</span>
                  <span className="text-zinc-500">ช่วงเวลา</span>
                  <span className="text-zinc-900 truncate">{slotLabelSplit(r) || slotLabel(r.time_slot)}</span>
                </div>
              </li>
            ))}
          </ul>
        </>
      )}
    </SectionShell>
  )
}


/* ─────────────────────────── 4. Report ─────────────────────────── */

function ReportSection({ cst, photos, token, approved, operation, regCount, oralHealth }) {
  const hasCst = cst.count > 0
  const hasPhotos = Array.isArray(photos) && photos.length > 0
  const hasOral = oralHealth && oralHealth.examined > 0

  return (
    <SectionShell id="report" icon={FileSearch} title="รายงาน"
      subtitle="สรุปผลการดำเนินงานและคะแนนความพึงพอใจจากพนักงานที่เข้ารับบริการ">

      <div className="flex justify-end -mt-2 mb-4">
        <button type="button" onClick={() => window.print()}
          className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-lg bg-amber-500 hover:bg-amber-600 text-white text-[13.5px] font-semibold shadow-sm shadow-amber-500/20">
          <Printer size={14} /> บันทึกเป็น PDF
        </button>
      </div>

      {/* Operation summary — facts about the event (when / how many days / how
          many patients). Not gated by approval; HR can always see this. */}
      {operation && <OperationSummary o={operation} patients={regCount} asOf={operation.treated_patients_at} />}

      {!approved ? (
        // Pre-approval: staff is still preparing the report. Show a clear
        // "pending" panel for the post-event data (photos + satisfaction).
        <div className="rounded-2xl ring-1 ring-amber-200 bg-amber-50/40 p-5 md:p-6 text-center">
          <FileSearch size={28} className="mx-auto text-amber-400" />
          <p className="mt-2.5 text-[14.5px] md:text-[15px] font-semibold text-zinc-800">รายงานยังอยู่ระหว่างการตรวจสอบ</p>
          <p className="mt-1 text-[13px] md:text-[13.5px] text-zinc-600 leading-relaxed max-w-[520px] mx-auto">
            ทีม Tooth Taxi กำลังตรวจสอบและรวบรวมข้อมูล — ภาพประกอบและคะแนนความพึงพอใจจะปรากฏที่นี่ทันทีหลังการอนุมัติ
          </p>
        </div>
      ) : (
        <>
          {hasPhotos
            ? <ReportPhotoSlider photos={photos} zipHref={api.publicSurvey.report.photosZipUrl(token)} />
            : (
              <div className="rounded-2xl ring-1 ring-zinc-200/70 bg-zinc-50/40 p-5 mb-5 text-center">
                <FileSearch size={26} className="mx-auto text-zinc-300" />
                <p className="mt-2 text-[14px] md:text-[14.5px] text-zinc-600">รายงานสรุปจากทีม Tooth Taxi จะถูกอัปโหลดที่นี่หลังจบกิจกรรม</p>
                <p className="text-[12px] md:text-[12.5px] text-zinc-400 mt-1">ปัจจุบันยังไม่มีรายงาน — แสดงเฉพาะคะแนนความพึงพอใจด้านล่างก่อน</p>
              </div>
            )}

          {hasOral && (
            <div className="mb-6">
              <OralHealthPanel data={oralHealth} />
            </div>
          )}

          {hasCst ? (
            <CstSatisfactionPanel cst={cst} />
          ) : (
            <div className="rounded-2xl ring-1 ring-zinc-200/70 bg-zinc-50/40 p-5 text-center">
              <Star size={26} className="mx-auto text-zinc-300" />
              <p className="mt-2 text-[14px] text-zinc-600">ยังไม่มีผลแบบประเมินความพึงพอใจ</p>
            </div>
          )}
        </>
      )}
    </SectionShell>
  )
}


function ReportPhotosSubsection({ photos, token }) {
  const [lightbox, setLightbox] = useState(null)   // { index }
  const total = photos.length

  return (
    <div className="mb-5">
      <SubsectionTitle>ภาพประกอบรายงาน</SubsectionTitle>
      <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
        <p className="text-[13.5px] md:text-[14px] text-zinc-600 leading-relaxed max-w-[520px]">
          ภาพจากหน้างานที่ทีม Tooth Taxi อัปโหลดไว้ — คลิกเพื่อดูภาพใหญ่ หรือดาวน์โหลดทั้งหมดเป็นไฟล์ zip
        </p>
        <a href={api.publicSurvey.report.photosZipUrl(token)}
          className="inline-flex items-center gap-1.5 px-3 py-2 rounded-md bg-amber-500 hover:bg-amber-600 text-white text-[13.5px] font-medium shadow-sm shadow-amber-500/20">
          <FolderDown size={13} /> ดาวน์โหลดทั้งหมด (.zip)
        </a>
      </div>
      <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 gap-2 md:gap-3">
        {photos.map((p, i) => (
          <button key={p.id} type="button" onClick={() => setLightbox({ index: i })}
            title={p.filename || 'photo'}
            className="block aspect-square rounded-lg overflow-hidden ring-1 ring-zinc-200 bg-zinc-100 hover:ring-amber-400 hover:shadow-sm transition">
            {p.url
              ? <img src={p.url} alt={p.filename} loading="lazy" className="w-full h-full object-cover" />
              : <div className="w-full h-full flex items-center justify-center text-zinc-400"><ImageIcon size={20} /></div>}
          </button>
        ))}
      </div>
      <p className="mt-2 text-[12px] text-zinc-400">{total.toLocaleString()} ภาพ</p>

      {lightbox && (
        <PhotoLightbox photos={photos} index={lightbox.index}
          onClose={() => setLightbox(null)}
          onChange={(idx) => setLightbox({ index: idx })} />
      )}
    </div>
  )
}

function PhotoLightbox({ photos, index, onClose, onChange }) {
  const p = photos[index]
  useEffect(() => {
    function onKey(e) {
      if (e.key === 'Escape')          onClose()
      else if (e.key === 'ArrowLeft')  onChange((index - 1 + photos.length) % photos.length)
      else if (e.key === 'ArrowRight') onChange((index + 1) % photos.length)
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [index, photos.length])
  if (!p) return null

  return (
    <div className="fixed inset-0 z-[80] flex flex-col bg-zinc-900/90 backdrop-blur-sm">
      <header className="flex items-center gap-2 px-3 md:px-4 py-2.5 bg-zinc-900/60 text-white">
        <span className="text-[13.5px] truncate flex-1 font-medium" title={p.filename}>
          {p.filename || 'photo'} <span className="text-white/50">· {index + 1}/{photos.length}</span>
        </span>
        {p.download_url && (
          <a href={p.download_url} download
            className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md text-[13px] bg-white/10 hover:bg-white/20 transition">
            <Download size={12} /> ดาวน์โหลด
          </a>
        )}
        <button onClick={onClose} className="w-8 h-8 rounded-md inline-flex items-center justify-center hover:bg-white/15">
          <X size={18} />
        </button>
      </header>
      <div className="flex-1 relative flex items-center justify-center p-3 md:p-6" onClick={onClose}>
        {photos.length > 1 && (
          <button onClick={(e) => { e.stopPropagation(); onChange((index - 1 + photos.length) % photos.length) }}
            className="absolute left-2 md:left-4 w-10 h-10 rounded-full bg-white/15 hover:bg-white/25 text-white flex items-center justify-center text-2xl leading-none">‹</button>
        )}
        <img src={p.url} alt={p.filename}
          onClick={(e) => e.stopPropagation()}
          className="max-w-full max-h-full object-contain rounded-md shadow-2xl" />
        {photos.length > 1 && (
          <button onClick={(e) => { e.stopPropagation(); onChange((index + 1) % photos.length) }}
            className="absolute right-2 md:right-4 w-10 h-10 rounded-full bg-white/15 hover:bg-white/25 text-white flex items-center justify-center text-2xl leading-none">›</button>
        )}
      </div>
    </div>
  )
}


/* ─────────────────────────── 5. Contact ─────────────────────────── */

const CONTACTS = [
  { name: 'เรด้า เกรียงศักดิ์', role: 'ผู้ประสานงานหลัก',  phone: '0990586699', email: '', primary: true },
  { name: 'เฟิสท์ ธัญญลักษณ์',  role: 'ผู้ประสานงานสำรอง', phone: '0815599842', email: '' },
]

function ContactSection() {
  return (
    <SectionShell id="contact" icon={Phone} title="ติดต่อทีม Tooth Taxi"
      subtitle="ทีม Tooth Taxi พร้อมดูแลและประสานงานกับคุณตลอดการให้บริการ — หากมีคำถามหรือต้องการความช่วยเหลือ ติดต่อทีมงานได้ตามช่องทางด้านล่าง">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {CONTACTS.map((c) => (
          <div key={c.name} className={'rounded-xl ring-1 p-3.5 md:p-4 bg-white ' +
            (c.primary ? 'ring-amber-300 bg-amber-50/30' : 'ring-zinc-200/70')}>
            <div className="flex items-start gap-3">
              <div className="w-11 h-11 md:w-12 md:h-12 rounded-full bg-amber-100 text-amber-700 inline-flex items-center justify-center text-[16px] md:text-[17px] font-bold shrink-0">
                {firstInitial(c.name)}
              </div>
              <div className="min-w-0 flex-1">
                <div className="text-[15px] font-semibold text-zinc-900 truncate">
                  {c.name} {c.primary && <span className="ml-1 text-[11px] uppercase tracking-wider text-amber-700 font-medium">primary</span>}
                </div>
                <div className="text-[12.5px] text-zinc-500">{c.role}</div>
                <div className="mt-2 space-y-1 text-[14px] text-zinc-700">
                  {c.phone
                    ? <a href={`tel:${c.phone}`} className="inline-flex items-center gap-1.5 hover:text-amber-700 active:bg-amber-50 rounded-md px-1 -mx-1 py-0.5"><Phone size={12} /> <span className="tabular-nums">{c.phone}</span></a>
                    : <span className="inline-flex items-center gap-1.5 text-zinc-400 italic"><Phone size={12} /> (ขอเบอร์โทรจากทีม)</span>}
                  {c.email && <div><a href={`mailto:${c.email}`} className="inline-flex items-center gap-1.5 hover:text-amber-700"><Mail size={12} /> {c.email}</a></div>}
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
    </SectionShell>
  )
}


/* ─────────────────────────── shared ─────────────────────────── */

function SectionShell({ id, icon: Icon, title, subtitle, children }) {
  return (
    <section id={id} className="bg-white rounded-2xl md:rounded-3xl shadow-[0_10px_40px_-20px_rgba(217,119,6,0.18)] ring-1 ring-amber-100/70 p-4 md:p-6">
      <h2 className="text-[16px] md:text-[20px] font-bold text-zinc-900 inline-flex items-center gap-2">
        <Icon size={17} className="text-amber-500" /> {title}
      </h2>
      {subtitle && (
        <p className="mt-1.5 mb-4 md:mb-5 text-[13.5px] md:text-[14.5px] text-zinc-600 leading-relaxed max-w-[680px]">
          {subtitle}
        </p>
      )}
      {!subtitle && <div className="mb-3 md:mb-4" />}
      {children}
    </section>
  )
}
function SubsectionTitle({ children }) {
  return (
    <h3 className="text-[15.5px] md:text-[17px] font-bold text-zinc-900 mb-2 flex items-center gap-2.5">
      <span className="inline-block w-1.5 h-5 md:h-6 rounded-full bg-amber-500" />
      {children}
    </h3>
  )
}
function Empty({ text }) {
  return <p className="text-[14px] text-zinc-400 italic px-1">{text}</p>
}
function Badge({ tone = 'zinc', icon: Icon, children }) {
  const cls = {
    amber:   'bg-amber-100 text-amber-800 ring-amber-200',
    emerald: 'bg-emerald-100 text-emerald-800 ring-emerald-200',
    rose:    'bg-rose-100 text-rose-800 ring-rose-200',
    zinc:    'bg-zinc-100 text-zinc-700 ring-zinc-200',
  }[tone] || ''
  return (
    <span className={'inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[11.5px] font-medium ring-1 ' + cls}>
      {Icon && <Icon size={10} />} {children}
    </span>
  )
}
function KpiCard({ icon: Icon, label, value, suffix, tone = 'amber' }) {
  return (
    <div className={'rounded-2xl ring-1 px-4 py-4 text-center bg-gradient-to-br ' + cardTone(tone)}>
      <Icon size={18} className="mx-auto text-zinc-600/70" />
      <div className="mt-1.5">
        <span className="text-[26px] md:text-[32px] font-extrabold tabular-nums text-zinc-900 leading-none">{value}</span>
        {suffix && <span className="ml-1 text-[13.5px] text-zinc-500 font-medium">{suffix}</span>}
      </div>
      <div className="mt-1.5 text-[12px] md:text-[12.5px] text-zinc-600 font-medium leading-snug">{label}</div>
    </div>
  )
}
function Shell({ children }) {
  return (
    <div className="min-h-screen bg-gradient-to-b from-amber-50 via-orange-50/40 to-white print:bg-none print:bg-white print:min-h-0">
      <div className="mx-auto px-3 md:px-5 py-3 md:py-6 max-w-[920px] print:max-w-none print:p-0">{children}</div>
    </div>
  )
}
function Centered({ children }) { return <div className="min-h-[60vh] flex items-center justify-center">{children}</div> }
function ErrorCard({ message }) {
  return (
    <div className="bg-white rounded-3xl ring-1 ring-rose-200/60 shadow-xl p-6 md:p-8 text-center">
      <AlertTriangle size={36} className="mx-auto text-rose-500" />
      <h2 className="mt-3 text-[17px] md:text-[20px] font-semibold text-zinc-900">ลิงก์รายงานนี้ใช้งานไม่ได้</h2>
      <p className="mt-1.5 text-[14.5px] text-zinc-500">{message}</p>
    </div>
  )
}
function Footer() {
  return (
    <p className="text-center text-[12px] text-zinc-400 mt-6 md:mt-8 pb-2">
      Powered by <span className="font-semibold text-amber-700">Tooth Taxi</span> · Dental Service on the Go
    </p>
  )
}


/* ─────────────────────────── A4 print / PDF ─────────────────────────── */

const PRINT_CSS = `
@media print {
  /* Margin is baked into the content padding (below) so it survives whatever
     the print dialog's "Margins" setting is — @page margin alone gets ignored. */
  @page { size: A4; margin: 0; }
  html, body { background: #fff !important; }
  * { -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; }
  #tt-report-print { position: static !important; left: auto !important; top: auto !important; width: 100% !important; }
  /* Keep whole cards / rows / images from splitting across a page break. */
  #tt-report-print .rounded-2xl, #tt-report-print .rounded-xl, #tt-report-print img, #tt-report-print li { break-inside: avoid; }
  #tt-report-print h1, #tt-report-print h2, #tt-report-print h3, #tt-report-print h4 { break-after: avoid; }
}
`

// Pick the highest-resolution photos as the "best" set (proxy for quality).
function pickBestPhotos(photos, n = 10) {
  const list = (photos || []).filter((p) => p && p.url)
  return [...list]
    .sort((a, b) => ((b.width || 0) * (b.height || 0)) - ((a.width || 0) * (a.height || 0)))
    .slice(0, n)
}

function todayThaiLong() {
  return new Date().toLocaleDateString('th-TH-u-ca-gregory', { day: '2-digit', month: 'long', year: 'numeric' })
}

// Off-screen on screen, full-flow when printing. Rendered (not display:none) so
// images preload before window.print() fires.
function ReportPrintArea({ data }) {
  return (
    <>
      <style>{PRINT_CSS}</style>
      <div id="tt-report-print" aria-hidden className="fixed top-0 left-[-9999px] w-[794px] bg-white text-zinc-900">
        <ReportPrintView data={data} />
      </div>
    </>
  )
}

function ReportPrintView({ data }) {
  const o = data.operation || {}
  const oral = data.oral_health
  const cst = data.cst
  const photos = pickBestPhotos(data.report_photos, 10)

  // p-[13mm] = the real page margin (baked in, dialog-proof).
  return (
    <div className="p-[13mm]">
      {/* Banner */}
      <img src="/report-header.png" alt="Tooth Taxi · Dental Service on the Go"
        className="w-full rounded-xl mb-6 rp-keep" />

      {/* Greeting / thank-you */}
      <div className="mb-7 rp-keep">
        <div className="text-[11px] uppercase tracking-[0.18em] text-amber-700 font-bold mb-1.5">รายงานสรุปการให้บริการ</div>
        <h1 className="text-[19px] font-extrabold text-zinc-900 leading-tight">เรียน {o.company_name}</h1>
        <p className="mt-2.5 text-[13px] md:text-[13.5px] text-zinc-700 leading-relaxed">
          ทีมงาน Tooth Taxi ขอขอบพระคุณเป็นอย่างสูง ที่ให้ความไว้วางใจเลือกใช้บริการรถทันตกรรมเคลื่อนที่ของเรา
          เราภาคภูมิใจอย่างยิ่งที่ได้เป็นส่วนหนึ่งในการดูแลรอยยิ้มและสุขภาพช่องปากที่ดีให้กับพนักงานทุกท่าน
          และหวังเป็นอย่างยิ่งว่าจะได้รับโอกาสอันดีในการกลับมาดูแลและส่งมอบบริการที่อบอุ่นแก่องค์กรของท่านอีกครั้งในโอกาสต่อไป
        </p>
        <div className="mt-3 flex flex-wrap items-center justify-between gap-2 border-t border-amber-200 pt-2.5">
          <span className="text-[12px] text-zinc-500">
            {fmtHeroRange(o.start_date, o.end_date, o.start_time, o.end_time)}
            {o.location_province ? ` · ${o.location_province}` : ''}
          </span>
          <span className="text-[12px] font-semibold text-amber-700">ด้วยความปรารถนาดี · ทีมงาน Tooth Taxi</span>
        </div>
      </div>

      {/* Operation summary */}
      <div className="rp-keep mb-5">
        <OperationSummary o={o} patients={o.treated_patients ?? 0} asOf={o.treated_patients_at} />
      </div>

      {/* Oral health */}
      {oral && oral.examined > 0 && (
        <div className="mb-6">
          <OralHealthPanel data={oral} />
        </div>
      )}

      {/* CST satisfaction */}
      {cst && cst.count > 0 && (
        <div className="mb-6">
          <CstSatisfactionPanel cst={cst} />
        </div>
      )}

      {/* Photos — masonry (best 10) */}
      {photos.length > 0 && (
        <div className="mb-4">
          <h3 className="text-[15.5px] font-bold text-zinc-900 mb-2.5 flex items-center gap-2.5">
            <span className="inline-block w-1.5 h-5 rounded-full bg-amber-500" /> ภาพประกอบรายงาน
          </h3>
          <div className="[column-count:3] [column-gap:8px]">
            {photos.map((p) => (
              <img key={p.id} src={p.url} alt={p.filename || ''}
                className="w-full mb-2 rounded-lg ring-1 ring-zinc-200 rp-keep" />
            ))}
          </div>
        </div>
      )}

      {/* Footer */}
      <div className="mt-6 pt-3 border-t border-zinc-200 flex items-center justify-between text-[10.5px] text-zinc-400">
        <span>จัดทำโดย Tooth Taxi · Dental Service on the Go</span>
        <span>พิมพ์เมื่อ {todayThaiLong()}</span>
      </div>
    </div>
  )
}

function FileTypeIcon({ contentType }) {
  if (contentType === 'application/pdf') return <FileType size={14} className="text-rose-500 shrink-0" />
  if (contentType?.startsWith('image/')) return <FileImage size={14} className="text-emerald-500 shrink-0" />
  return <FileText size={14} className="text-zinc-400 shrink-0" />
}
function displayLabel(r) { return r.custom_label || r.kind_name }
function meanTone(v) {
  if (v == null) return 'zinc'
  if (v < 3) return 'rose'
  if (v < 4) return 'amber'
  return 'emerald'
}
function toneClass(t) {
  return ({
    rose:    'bg-rose-50 ring-rose-200 text-rose-700',
    amber:   'bg-amber-50 ring-amber-200 text-amber-800',
    emerald: 'bg-emerald-50 ring-emerald-200 text-emerald-700',
    zinc:    'bg-zinc-50 ring-zinc-200 text-zinc-600',
  })[t] || ''
}
function barClass(t) {
  return ({
    rose:    'bg-rose-400',
    amber:   'bg-amber-400',
    emerald: 'bg-emerald-400',
    zinc:    'bg-zinc-300',
  })[t] || ''
}
function meanTextClass(t) {
  return ({
    rose:    'text-rose-600',
    amber:   'text-amber-600',
    emerald: 'text-emerald-600',
    zinc:    'text-zinc-500',
  })[t] || 'text-zinc-900'
}
function cardTone(t) {
  return ({
    amber:   'from-amber-50 to-orange-50 ring-amber-200/70',
    emerald: 'from-emerald-50 to-teal-50 ring-emerald-200/70',
    rose:    'from-rose-50 to-pink-50 ring-rose-200/70',
    zinc:    'from-zinc-50 to-white ring-zinc-200/70',
  })[t] || 'from-amber-50 to-orange-50 ring-amber-200/70'
}

function firstInitial(s) {
  if (!s) return '?'
  for (const ch of String(s).trim()) {
    const cp = ch.codePointAt(0)
    if (cp >= 0x0E01 && cp <= 0x0E2E) return ch
    if ((cp >= 0x41 && cp <= 0x5A) || (cp >= 0x61 && cp <= 0x7A)) return ch
  }
  return [...String(s).trim()][0] || '?'
}
// HR-portal hero date/time format. Per spec: "start date - end date, start time - end time".
//   Same day  → "30 พ.ค. 2026, 09:00 - 17:00"
//   Multi-day → "30 พ.ค. 2026 - 31 พ.ค. 2026, 09:00 - 17:00"
// Falls back gracefully when end_date / times are missing.
function fmtHeroRange(startDate, endDate, startTime, endTime) {
  if (!startDate) return '—'
  const sd = fmtDateOnly(startDate)
  const datePart = (endDate && endDate !== startDate)
    ? `${sd} - ${fmtDateOnly(endDate)}`
    : sd
  if (!startTime && !endTime) return datePart
  const timePart = `${startTime || '?'} - ${endTime || '?'}`
  return `${datePart}, ${timePart}`
}

function slotLabel(value) {
  if (!value || !value.includes('|')) return value || '—'
  const [a, b] = value.split('|')
  try {
    const da = new Date(a); const db = new Date(b)
    return `${da.toLocaleString('th-TH-u-ca-gregory', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })} – ${db.toLocaleTimeString('th-TH-u-ca-gregory', { hour: '2-digit', minute: '2-digit' })}`
  } catch { return value }
}
// Prefer the split columns when present (post-migration 032). Format:
// "1 มิ.ย. 2569 · 09:00-10:00". Returns null when split fields aren't set yet,
// letting the caller fall back to slotLabel(time_slot).
function slotLabelSplit(r) {
  if (!r?.slot_date || !r?.slot_time_period) return null
  const [y, m, d] = r.slot_date.split('-').map(Number)
  try {
    const date = new Date(Date.UTC(y, m - 1, d))
      .toLocaleDateString('th-TH-u-ca-gregory', { day: '2-digit', month: 'short', year: 'numeric', timeZone: 'UTC' })
    return `${date} · ${r.slot_time_period}`
  } catch { return `${r.slot_date} · ${r.slot_time_period}` }
}
function fmtFull(unix) {
  if (!unix) return '—'
  const d = new Date(unix * 1000)
  return d.toLocaleDateString('th-TH-u-ca-gregory', { day: '2-digit', month: 'short', year: 'numeric' }) + ' · ' +
    d.toLocaleTimeString('th-TH-u-ca-gregory', { hour: '2-digit', minute: '2-digit' })
}
function fileToDataUri(file) {
  return new Promise((resolve, reject) => {
    const r = new FileReader()
    r.onload  = () => resolve(String(r.result || ''))
    r.onerror = () => reject(new Error('read failed'))
    r.readAsDataURL(file)
  })
}
function extractErr(e) {
  const m = (e?.message || '').match(/:\s*\{?"?error"?"?:\s*"?([^"}\n]+)/)
  return m ? m[1] : (e?.message || 'error')
}
